import { useQuery } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";
import { downloadReportCsv } from "./reports";

export interface AuditLogRecord {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  ipAddress: string | null;
  hash: string | null;
  createdAt: string;
  user: { id: string; fullName: string; email: string } | null;
}

export interface AuditLogFilters {
  entityType?: string;
  action?: string;
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export function useAuditLogs(page: number, filters: AuditLogFilters = {}, options: { enabled?: boolean; pageSize?: number } = {}) {
  return useQuery({
    queryKey: ["audit", page, filters, options.pageSize],
    enabled: options.enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<AuditLogRecord[]>>("/audit", {
        params: { page, pageSize: options.pageSize ?? 20, ...filters },
      });
      return data;
    },
  });
}

export async function exportAuditLogsCsv(filters: AuditLogFilters = {}) {
  await downloadReportCsv("/audit/export.csv", "auditoria.csv", filters as Record<string, string | undefined>);
}

export interface AuditChainVerificationResult {
  organizationId: string;
  totalRows: number;
  hashedRows: number;
  unhashedRows: number;
  valid: boolean;
  firstBrokenRow: { id: string; createdAt: string; reason: string } | null;
}

export function useAuditChainIntegrity(enabled: boolean) {
  return useQuery({
    queryKey: ["audit", "verify-integrity"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<AuditChainVerificationResult>>("/audit/verify-integrity");
      return data.data;
    },
    enabled,
    // Verificación bajo demanda, no en cada render: el usuario dispara el
    // refetch con un botón ("Verificar integridad ahora").
    staleTime: Infinity,
  });
}

export interface AuditArchiveCheckpointRecord {
  id: string;
  archivedThroughDate: string;
  rowsArchived: number;
  archiveFile: string;
  createdAt: string;
}

export interface AuditRetentionStatus {
  retentionDays: number;
  archiveEnabled: boolean;
  rowsPendingArchive: number;
  cutoffDate: string | null;
  checkpoints: AuditArchiveCheckpointRecord[];
}

export function useAuditRetentionStatus() {
  return useQuery({
    queryKey: ["audit", "retention"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<AuditRetentionStatus>>("/audit/retention");
      return data.data;
    },
  });
}
